// src/pages/MockExamLandingPage.jsx
//
// Public, indexable landing page for a single exam: /:lang/mock-exams/telc-b1 etc.
//
// WHAT THIS PAGE IS FOR
// Logami's exam content sits behind auth and is Disallowed in robots.txt, so none
// of it was reachable by search engines or AI crawlers. These pages expose the
// parts people actually search for (format, timing, scoring, section breakdown) as
// static prerendered HTML, and funnel to the no-signup guest exam at /try. Every
// number comes from src/data/mockExamCatalog.js, which is build-time verified
// against the exam JSON (npm run verify:exam-catalog).
//
// DESIGN
// This page is a sibling of MockExamIndexPage, so it wears the same brand as
// HomePageB: warm stone palette, amber accent (#b45309), Space Grotesk set heavy
// and tight, the hand-drawn underline on the accent phrase, oversized numerals,
// hairline stone rules instead of heavy card borders. It previously used the
// blog's black-and-blue card treatment, which read as a different product the
// moment you clicked through from the hub.
//
// The FAQ rows collapse visually but stay in the DOM, the same trick HomePageB
// uses, so crawlers and AI retrievers read every answer without running the toggle.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUp, Check, ArrowRight } from 'lucide-react';
import { useLangParam } from '../hooks/useLocalizedNavigate';
import { getMockExam, MOCK_EXAM_CATALOG, formatDuration, paperTermDe } from '../data/mockExamCatalog';
import { paperPreview } from '../data/mockExamPapers';
import { trackConversionEvent } from '../utils/conversionEvents';
import HandDrawnLine from '../components/ui/HandDrawnLine';
import ExamShowcase from '../components/mockExam/ExamShowcase';
import RedPenCards from '../components/mockExam/RedPenCards';
import { getExamShowcase, getRedPen } from '../data/examShowcase';

const BASE_URL = 'https://logami.app';
const PASS_MARK_PERCENT = 60;

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' };
const NUMERAL = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.05em' };

/** Blog guide slug for an exam, e.g. 'telc-b2' -> 'telc-b2-exam-guide'. */
const guideSlug = (slug) => `${slug}-exam-guide`;

/** Redemittel post slug for an exam, e.g. 'osd-b2' -> 'osd-b2-redemittel-schreiben'. */
const redemittelSlug = (slug) => `${slug}-redemittel-schreiben`;

/** Sprechen sample-exam post for an exam, e.g. 'goethe-c1' -> 'goethe-c1-sprechen-sample-exams'. */
const sprechenSlug = (slug) => `${slug}-sprechen-sample-exams`;

/** Sections with 0 minutes are worked inside another section's clock (e.g. telc
 *  Sprachbausteine shares the 90-minute reading block), so say so rather than
 *  printing a misleading "0 min". */
function SectionMinutes({ minutes, sharedLabel }) {
  if (minutes > 0) return <>{formatDuration(minutes)}</>;
  return <span className="text-stone-400 italic">{sharedLabel}</span>;
}

function Stat({ value, label, sub }) {
  return (
    <div>
      <div
        className="text-[3rem] sm:text-[3.75rem] lg:text-[4.25rem] leading-[0.9] font-bold text-stone-900 tabular-nums"
        style={NUMERAL}
      >
        {value}
      </div>
      <div className="mt-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-stone-500">
        {label}
      </div>
      {sub && <div className="mt-1 text-[13px] text-stone-500 leading-snug">{sub}</div>}
    </div>
  );
}

/** Collapsed for the eye, present for the crawler. Same contract as HomePageB's
 *  FaqItem: the answer never leaves the DOM, it only goes sr-only. */
function FaqRow({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-200 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full min-h-[48px] flex items-start justify-between gap-4 py-5 text-left cursor-pointer group"
      >
        <h3 className="text-[15px] sm:text-base font-semibold text-stone-900 leading-snug group-hover:text-stone-600 transition-colors duration-200">
          {question}
        </h3>
        <ChevronUp
          className={`w-4 h-4 mt-1 flex-shrink-0 text-stone-400 transition-transform duration-200 ${open ? '' : 'rotate-180'}`}
          aria-hidden="true"
        />
      </button>
      <div className={open ? 'pb-5' : 'sr-only'}>
        <p className="text-[14px] sm:text-[15px] text-stone-500 leading-relaxed max-w-2xl">{answer}</p>
      </div>
    </div>
  );
}

const MockExamLandingPage = () => {
  const { examSlug } = useParams();
  const { t } = useTranslation();
  const lang = useLangParam();
  const exam = getMockExam(examSlug);

  const canonicalUrl = `${BASE_URL}/${lang}/mock-exams/${examSlug}`;
  const title = exam
    ? t('mockExam.meta.title', {
        seoName: exam.seoName,
        defaultValue: '{{seoName}} Modelltest Online: Free Mock Exam with Answers | Logami',
      })
    : '';
  const description = exam
    ? t('mockExam.meta.description', {
        examName: exam.examName,
        seoName: exam.seoName,
        minutes: exam.totalMinutes,
        variants: exam.variants,
        defaultValue:
          '{{variants}} complete {{seoName}} Modelltests in the official format: all sections, {{minutes}} minutes of working time, with the correct answers shown the moment you submit.',
      })
    : '';

  const faqs = useMemo(() => {
    if (!exam) return [];
    const v = {
      examName: exam.examName,
      seoName: exam.seoName,
      level: exam.level,
      provider: exam.providerLabel,
      minutes: exam.totalMinutes,
      duration: formatDuration(exam.totalMinutes),
      points: exam.totalPoints,
      variants: exam.variants,
      pass: PASS_MARK_PERCENT,
      paperTerm: paperTermDe(exam),
    };
    return [
      {
        q: t('mockExam.faq.duration.q', { ...v, defaultValue: 'How long does the {{seoName}} exam take?' }),
        a: t('mockExam.faq.duration.a', {
          ...v,
          defaultValue:
            'The {{examName}} takes {{duration}} of working time in total across all sections, excluding breaks and the preparation time before the oral exam.',
        }),
      },
      {
        q: t('mockExam.faq.pass.q', { ...v, defaultValue: 'What score do I need to pass the {{seoName}}?' }),
        a:
          exam.passing === 'modular'
            ? t('mockExam.faq.pass.a.modular', {
                ...v,
                defaultValue:
                  'You need {{pass}}% to pass. Each of the four modules is scored independently out of 100 points, so you can pass, and retake, modules one at a time.',
              })
            : t('mockExam.faq.pass.a.separate', {
                ...v,
                defaultValue:
                  'You need {{pass}}% overall, out of {{points}} points. The written and oral parts are graded separately and you must reach {{pass}}% in each. Passing one does not compensate for failing the other.',
              }),
      },
      {
        q: t('mockExam.faq.free.q', { ...v, defaultValue: 'Can I take a {{seoName}} mock exam for free?' }),
        a: t('mockExam.faq.free.a', {
          ...v,
          defaultValue:
            'Yes. You can start a full mock exam on Logami without creating an account. You only need a free account to see the full scored breakdown of your answers.',
        }),
      },
      {
        q: t('mockExam.faq.count.q', { ...v, defaultValue: 'How many {{seoName}} practice tests are there?' }),
        a: t('mockExam.faq.count.a', {
          ...v,
          defaultValue:
            'Logami has {{variants}} complete {{examName}} papers. Each follows the same official format but uses different reading texts, listening recordings and questions, so you can sit the exam repeatedly without repeating content.',
        }),
      },
      {
        q: t('mockExam.faq.pdf.q', {
          ...v,
          defaultValue: 'Is there a {{seoName}} Modelltest PDF with answers?',
        }),
        a: t('mockExam.faq.pdf.a', {
          ...v,
          defaultValue:
            'Logami’s papers are not PDFs. They run in the browser, mark themselves, and show the correct answer beside yours as soon as you submit, so there is nothing to print or check by hand. If you specifically want a paper to print, {{provider}} publishes a free official {{paperTerm}} on its own website.',
        }),
      },
      {
        q: t('mockExam.faq.difference.q', {
          ...v,
          defaultValue: 'What is the difference between a Modelltest and the real Prüfung?',
        }),
        a: t('mockExam.faq.difference.a', {
          ...v,
          defaultValue:
            'The Prüfung is the exam you sit at a licensed centre for a certificate. A Modelltest is a full-length practice paper in exactly that format: the same sections in the same order, the same timing and the same point split. Logami has {{variants}} {{seoName}} Modelltests, so nothing about the format is new to you on the day.',
        }),
      },
      {
        q: t('mockExam.faq.realistic.q', { ...v, defaultValue: 'Are these mock exams like the real exam?' }),
        a: t('mockExam.faq.realistic.a', {
          ...v,
          defaultValue:
            'They follow the official section structure, timing, task types and scoring shown in the table above, and run against a live clock. They are full-length simulations rather than shortened practice exercises. Logami is independent and not affiliated with {{provider}}.',
        }),
      },
      {
        q: t('mockExam.faq.writing.q', { ...v, defaultValue: 'Is my writing corrected?' }),
        a: t('mockExam.faq.writing.a', {
          ...v,
          defaultValue:
            'Yes. Submit your Schreiben response and you receive a score and feedback modelled on the official examiner rubric for {{level}}. This is AI-generated feedback, not a human examiner.',
        }),
      },
    ];
  }, [exam, t]);

  // Meta + JSON-LD. Prerendering covers the first paint for crawlers; this keeps
  // the tags correct during client-side navigation too.
  useEffect(() => {
    if (!exam) return;
    const prevTitle = document.title;
    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    if (metaDesc) metaDesc.setAttribute('content', description);

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LearningResource',
          name: `${exam.examName} Modelltest`,
          description,
          url: canonicalUrl,
          learningResourceType: 'Practice exam',
          educationalLevel: exam.level,
          inLanguage: 'de',
          teaches: `${exam.examName} exam preparation`,
          timeRequired: `PT${exam.totalMinutes}M`,
          isAccessibleForFree: true,
          provider: { '@type': 'Organization', name: 'Logami', url: BASE_URL },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            // The hub exists in English only, so its canonical URL is the /en one in every language.
            { '@type': 'ListItem', position: 1, name: t('mockExam.breadcrumb', { defaultValue: 'Mock exams' }), item: `${BASE_URL}/en/mock-exams` },
            { '@type': 'ListItem', position: 2, name: exam.examName, item: canonicalUrl },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-mock-exam-ld', '1');
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      script.remove();
    };
  }, [exam, title, description, canonicalUrl, lang, faqs, t]);

  if (!exam) {
    return (
      <div className="bg-stone-50">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-stone-900 tracking-tighter mb-6" style={DISPLAY}>
            {t('mockExam.notFound.title', { defaultValue: 'Exam not found' })}
          </h1>
          <Link
            to={`/${lang}/mock-exams`}
            className="inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-md shadow-stone-900/10 hover:bg-stone-800 active:scale-[0.97] transition-colors duration-200 cursor-pointer"
            style={DISPLAY}
          >
            {t('mockExam.notFound.cta', { defaultValue: 'See all mock exams' })}
          </Link>
        </div>
      </div>
    );
  }

  const trackStartFree = () => {
    trackConversionEvent('mock_exam_page_cta_click', {
      exam_provider: exam.provider,
      exam_level: exam.level,
      exam_slug: exam.slug,
    });
  };

  const sharedLabel = t('mockExam.format.shared', { defaultValue: 'shared clock' });
  const paperTerm = paperTermDe(exam);
  // A glimpse of the actual papers. Four rows, because the point is to show what a
  // paper is about, not to hand the whole library to someone who has not signed up.
  // ÖSD rows carry no topic line yet, so they render as the city alone.
  const previewPapers = paperPreview(exam.provider, exam.level, 4);
  // ÖSD calls its own paper a Modelltest, which is also the word searchers type for
  // all three publishers. Using the generic sentence there produced "Modelltest,
  // Modelltest or Prüfung?" and a paragraph that defined the same word twice, so
  // ÖSD gets its own wording.
  const publisherWordIsGeneric = paperTerm === 'Modelltest';
  const remainingPapers = Math.max(exam.variants - previewPapers.length, 0);
  // Link every sibling exam, not the first four. These pages are each other's only
  // meaningful inbound links, and the cluster was sitting at GSC position 32 to 62
  // with nothing pointing into it. Nine pages is a small enough set to link in full.
  const related = MOCK_EXAM_CATALOG.filter((e) => e.slug !== exam.slug);

  const included = [
    t('mockExam.included.papers', {
      variants: exam.variants,
      defaultValue: '{{variants}} complete papers, each with different texts and questions',
    }),
    t('mockExam.included.timed', { defaultValue: 'Timed simulator matching the official section structure' }),
    t('mockExam.included.writing', { defaultValue: 'Schreiben feedback scored against the examiner rubric' }),
    t('mockExam.included.drills', { defaultValue: 'Separate Lesen, Hören and Schreiben drills for one weak section' }),
    t('mockExam.included.redemittel', { defaultValue: 'Redemittel phrase guides for writing and speaking' }),
    t('mockExam.included.plan', { defaultValue: 'A study plan built backwards from your exam date' }),
  ];


  // Shared by the scroll showcase (exams that have one) and the plain hero.
  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="text-[13px] text-stone-500">
      <Link
        to={`/${lang}/mock-exams`}
        className="hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200"
      >
        {t('mockExam.breadcrumb', { defaultValue: 'Mock exams' })}
      </Link>
      <span className="mx-2" aria-hidden="true">/</span>
      <span className="text-stone-700">{exam.examName}</span>
    </nav>
  );
  const heading = (sizes) => (
    <h1 className={`${sizes} tracking-tighter text-stone-900 font-bold`} style={DISPLAY}>
      {exam.seoName}{' '}
      <HandDrawnLine>
        <span className="font-bold text-stone-900">
          {t('mockExam.hero.accent', { defaultValue: 'Modelltest Online' })}
        </span>
      </HandDrawnLine>
    </h1>
  );
  const intro = t('mockExam.hero.intro', {
    examName: exam.examName,
    duration: formatDuration(exam.totalMinutes),
    count: exam.sections.length,
    variants: exam.variants,
    defaultValue:
      'Sit the complete {{examName}} in the official format: all {{count}} sections, {{duration}} of working time, against a live clock. {{variants}} full papers are available, each with different texts and questions, and every marked question shows you the correct answer at the end.',
  });
  // `pinned`: the showcase keeps this pinned under the stage on phones, where
  // only the full-width button fits; pricing stays reachable from the header.
  const ctas = (pinned = false) => (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <Link
        to={`/${lang}/try`}
        onClick={trackStartFree}
        className={`${pinned ? 'w-full lg:w-auto justify-center' : ''} inline-flex items-center gap-2 min-h-[48px] px-6 py-3 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-md shadow-stone-900/10 hover:bg-stone-800 active:scale-[0.97] transition-colors duration-200 cursor-pointer`}
        style={DISPLAY}
      >
        {t('mockExam.cta.button', { defaultValue: 'Start a free mock exam' })}
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </Link>
      <Link
        to={`/${lang}/pricing`}
        className={`${pinned ? 'hidden lg:inline-flex' : 'inline-flex'} items-center min-h-[44px] text-[14px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer`}
      >
        {t('mockExam.cta.pricing', { defaultValue: 'See pricing' })}
      </Link>
    </div>
  );
  // A replay of one real paper, for the exams that have one (src/data/examShowcase.js).
  const showcase = getExamShowcase(exam.slug);
  const redPen = getRedPen(showcase);

  return (
    <div className="bg-stone-50">
      <article>
        {showcase ? (
          <>
            <ExamShowcase
              exam={exam}
              showcase={showcase}
              hero={
                <>
                  {breadcrumb}
                  {heading('text-[34px] max-lg:[@media(max-height:700px)]:text-[27px] leading-[1.04] lg:text-[3.375rem] lg:leading-[1.03]')}
                  <p className="max-w-md text-[14px] lg:text-base text-stone-500 leading-normal lg:leading-relaxed line-clamp-2 max-lg:[@media(max-height:700px)]:line-clamp-1 lg:line-clamp-none">
                    {intro}
                  </p>
                </>
              }
              cta={ctas(true)}
            />
            {redPen && (
              <RedPenCards
                exam={exam}
                data={redPen}
                grammarHref={`/${lang}/grammar/${redPen.level}`}
                redemittelHref={`/${lang}/blog/${redemittelSlug(exam.slug)}`}
              />
            )}
          </>
        ) : (
          <>
            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-10 sm:pt-14 sm:pb-14">
              <div className="mb-6">{breadcrumb}</div>
              {heading('text-[2rem] leading-[1.08] sm:text-[3rem] sm:leading-[1.05] lg:text-[3.5rem] max-w-3xl')}
              <p className="mt-7 max-w-xl text-[15px] sm:text-base text-stone-500 leading-relaxed">{intro}</p>
              <div className="mt-7">{ctas()}</div>
            </section>

            {/* The numbers, set the way the homepage sets numbers. */}
            <section className="border-y border-stone-200 bg-white" aria-labelledby="stats-heading">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <h2 id="stats-heading" className="sr-only">
                  {t('mockExam.stats.heading', { seoName: exam.seoName, defaultValue: '{{seoName}} at a glance' })}
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
                  <Stat
                    value={exam.totalMinutes}
                    label={t('mockExam.stats.duration', { defaultValue: 'Minutes' })}
                    sub={t('mockExam.stats.durationSub', {
                      defaultValue: 'Working time across all sections.',
                    })}
                  />
                  <Stat
                    value={exam.totalPoints}
                    label={t('mockExam.stats.points', { defaultValue: 'Total points' })}
                    sub={t('mockExam.stats.pointsSub', {
                      pass: PASS_MARK_PERCENT,
                      defaultValue: 'You need {{pass}}% of them to pass.',
                    })}
                  />
                  <Stat
                    value={exam.sections.length}
                    label={t('mockExam.stats.sections', { defaultValue: 'Sections' })}
                    sub={t('mockExam.stats.sectionsSub', {
                      defaultValue: 'Each on its own official clock.',
                    })}
                  />
                  <Stat
                    value={exam.variants}
                    label={t('mockExam.stats.papers', { defaultValue: 'Modelltests' })}
                    sub={t('mockExam.stats.papersSub', {
                      defaultValue: 'Every one a complete paper, not an extract.',
                    })}
                  />
                </div>
              </div>
            </section>
          </>
        )}

        {/* Format */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16" aria-labelledby="format-heading">
          <h2
            id="format-heading"
            className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold"
            style={DISPLAY}
          >
            {t('mockExam.format.heading', {
              seoName: exam.seoName,
              defaultValue: '{{seoName}} exam format',
            })}
          </h2>

          <div className="mt-8 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            <table className="w-full text-left border-collapse min-w-[520px]">
              <caption className="sr-only">
                {t('mockExam.format.caption', {
                  examName: exam.examName,
                  defaultValue: 'Sections, duration and points for the {{examName}} exam',
                })}
              </caption>
              <thead>
                <tr className="border-b border-stone-200">
                  <th scope="col" className="px-5 py-4 text-[12px] font-semibold uppercase tracking-[0.12em] text-stone-500">
                    {t('mockExam.format.section', { defaultValue: 'Section' })}
                  </th>
                  <th scope="col" className="px-5 py-4 text-[12px] font-semibold uppercase tracking-[0.12em] text-stone-500">
                    {t('mockExam.format.english', { defaultValue: 'English' })}
                  </th>
                  <th scope="col" className="px-5 py-4 text-[12px] font-semibold uppercase tracking-[0.12em] text-stone-500">
                    {t('mockExam.format.time', { defaultValue: 'Time' })}
                  </th>
                  <th scope="col" className="px-5 py-4 text-[12px] font-semibold uppercase tracking-[0.12em] text-stone-500">
                    {t('mockExam.format.points', { defaultValue: 'Points' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {exam.sections.map((s) => (
                  <tr key={s.id} className="border-b border-stone-200">
                    <th scope="row" className="px-5 py-4 text-[15px] font-bold text-stone-900" style={DISPLAY}>
                      {s.de}
                    </th>
                    <td className="px-5 py-4 text-[14px] text-stone-500">{s.en}</td>
                    <td className="px-5 py-4 text-[14px] text-stone-500 tabular-nums">
                      <SectionMinutes minutes={s.minutes} sharedLabel={sharedLabel} />
                    </td>
                    <td className="px-5 py-4 text-[14px] text-stone-500 tabular-nums">{s.points}</td>
                  </tr>
                ))}
                <tr className="bg-stone-50">
                  <th scope="row" className="px-5 py-4 text-[15px] font-bold text-stone-900" style={DISPLAY}>
                    {t('mockExam.format.total', { defaultValue: 'Total' })}
                  </th>
                  <td className="px-5 py-4" />
                  <td className="px-5 py-4 text-[14px] font-bold text-stone-900 tabular-nums">
                    {formatDuration(exam.totalMinutes)}
                  </td>
                  <td className="px-5 py-4 text-[14px] font-bold text-stone-900 tabular-nums">{exam.totalPoints}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-[14px] text-stone-500 leading-relaxed max-w-2xl">
            {exam.passing === 'modular'
              ? t('mockExam.format.noteModular', {
                  pass: PASS_MARK_PERCENT,
                  defaultValue:
                    'Pass mark: {{pass}}%. Each module is scored independently and can be retaken on its own.',
                })
              : t('mockExam.format.noteSeparate', {
                  pass: PASS_MARK_PERCENT,
                  defaultValue:
                    'Pass mark: {{pass}}%. The written and oral parts are graded separately and each must reach {{pass}}%.',
                })}
          </p>

          {/* Strategy lives in the blog guide, which already ranks. Link, never restate.
              The Redemittel post is the other half of the cluster: it is where most
              German-market searchers land, and two of these posts were unindexed
              until they had a link from a page Google already ranks. The Sprechen
              sample post completes the cluster for the oral exam. */}
          <p className="mt-5 flex flex-wrap gap-x-6">
            <Link
              to={`/${lang}/blog/${guideSlug(exam.slug)}`}
              className="inline-flex items-center min-h-[44px] text-[14px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer"
            >
              {t('mockExam.format.guide', {
                seoName: exam.seoName,
                defaultValue: 'Read the {{seoName}} exam guide',
              })}
            </Link>
            <Link
              to={`/${lang}/blog/${redemittelSlug(exam.slug)}`}
              className="inline-flex items-center min-h-[44px] text-[14px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer"
            >
              {t('mockExam.format.redemittel', {
                seoName: exam.seoName,
                defaultValue: '{{seoName}} Redemittel for Schreiben and Sprechen (free PDF)',
              })}
            </Link>
            <Link
              to={`/${lang}/blog/${sprechenSlug(exam.slug)}`}
              className="inline-flex items-center min-h-[44px] text-[14px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer"
            >
              {t('mockExam.format.sprechen', {
                seoName: exam.seoName,
                defaultValue: '{{seoName}} Sprechen: sample oral exams with model dialogues',
              })}
            </Link>
          </p>
        </section>

        {/* The papers themselves */}
        <section className="border-y border-stone-200 bg-white" aria-labelledby="papers-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2
              id="papers-heading"
              className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold"
              style={DISPLAY}
            >
              {t('mockExam.papers.heading', {
                seoName: exam.seoName,
                defaultValue: 'Inside the {{seoName}} Modelltests',
              })}
            </h2>
            <p className="mt-3 max-w-xl text-[15px] text-stone-500 leading-relaxed">
              {t('mockExam.papers.intro', {
                variants: exam.variants,
                defaultValue:
                  'Each paper carries its own texts, recordings and tasks. Here are the first few of the {{variants}}.',
              })}
            </p>

            <ul className="mt-8 list-none pl-0 m-0 max-w-3xl">
              {previewPapers.map((paper) => (
                <li
                  key={paper.name}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-4 border-b border-stone-200"
                >
                  <span
                    className="text-base font-bold text-stone-900 sm:w-32 sm:shrink-0"
                    style={DISPLAY}
                  >
                    {paper.name}
                  </span>
                  {paper.topic && (
                    <span className="text-[15px] text-stone-500 leading-relaxed">{paper.topic}</span>
                  )}
                </li>
              ))}
            </ul>

            {remainingPapers > 0 && (
              <p className="mt-5 text-[14px] text-stone-500 leading-relaxed">
                {t('mockExam.papers.more', {
                  count: remainingPapers,
                  seoName: exam.seoName,
                  defaultValue: 'Another {{count}} {{seoName}} papers are waiting inside.',
                })}{' '}
                <Link
                  to={`/${lang}/try`}
                  onClick={trackStartFree}
                  className="font-semibold text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer"
                >
                  {t('mockExam.papers.cta', { defaultValue: 'Sit the first one free' })}
                </Link>
              </p>
            )}
          </div>
        </section>

        {/* Terminology, and the PDF question. Every autocomplete for these exams ends
            in "pdf" or "mit Lösungen", and the honest answer (we are not a PDF, here
            is what we are instead) is also the answer that carries the searched words. */}
        <section className="border-y border-stone-200 bg-white" aria-labelledby="terms-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2
              id="terms-heading"
              className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold max-w-2xl"
              style={DISPLAY}
            >
              {publisherWordIsGeneric
                ? t('mockExam.terms.headingGeneric', { defaultValue: 'Modelltest or Prüfung?' })
                : t('mockExam.terms.heading', {
                    paperTerm,
                    defaultValue: 'Modelltest, {{paperTerm}} or Prüfung?',
                  })}
            </h2>
            <div className="mt-6 space-y-4 max-w-2xl">
              <p className="text-[15px] text-stone-500 leading-relaxed">
                {publisherWordIsGeneric
                  ? t('mockExam.terms.officialGeneric', {
                      provider: exam.providerLabel,
                      seoName: exam.seoName,
                      variants: exam.variants,
                      defaultValue:
                        'The Prüfung is the real exam, sat at a licensed centre. {{provider}} publishes one official Modelltest so you can see the format for yourself, free to download from their own site. Logami’s {{variants}} {{seoName}} papers are built to that same format, with their own texts and tasks.',
                    })
                  : t('mockExam.terms.official', {
                      provider: exam.providerLabel,
                      seoName: exam.seoName,
                      paperTerm,
                      variants: exam.variants,
                      defaultValue:
                        'The Prüfung is the real exam, sat at a licensed centre. The {{paperTerm}} is the official sample paper {{provider}} publishes so you can see the format for yourself, free to download from their own site. A Modelltest is any full-length paper built to that same format, which is what Logami’s {{variants}} {{seoName}} papers are.',
                    })}
              </p>
              <p className="text-[15px] text-stone-500 leading-relaxed">
                {t('mockExam.terms.pdf', {
                  defaultValue:
                    'Logami’s papers are not PDFs. They run in the browser on the official clock, mark themselves, and put the correct answer next to yours when you submit. That is the part a printed Modelltest mit Lösungen cannot do.',
                })}
              </p>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16" aria-labelledby="included-heading">
          <h2
            id="included-heading"
            className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold"
            style={DISPLAY}
          >
            {t('mockExam.included.heading', {
              level: exam.level,
              defaultValue: 'What you get for {{level}}',
            })}
          </h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-12 max-w-4xl list-none pl-0">
            {included.map((item) => (
              <li key={item} className="flex gap-3 py-4 border-b border-stone-200">
                <Check className="w-4 h-4 text-[#b45309] shrink-0 mt-1" aria-hidden="true" />
                <span className="text-[15px] text-stone-600 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="border-y border-stone-200 bg-white" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2
              id="faq-heading"
              className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold mb-8"
              style={DISPLAY}
            >
              {t('mockExam.faq.heading', {
                seoName: exam.seoName,
                defaultValue: '{{seoName}} questions, answered',
              })}
            </h2>
            <div className="rounded-2xl border border-stone-200 bg-white px-5 sm:px-7">
              {faqs.map((f) => (
                <FaqRow key={f.q} question={f.q} answer={f.a} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2
            className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold max-w-2xl"
            style={DISPLAY}
          >
            {t('mockExam.cta.heading', {
              seoName: exam.seoName,
              defaultValue: 'Sit a full {{seoName}} Modelltest',
            })}
          </h2>
          <p className="mt-4 max-w-xl text-[15px] text-stone-500 leading-relaxed">
            {t('mockExam.cta.body', {
              defaultValue:
                'Start now without creating an account. You only need a free account to see your full scored breakdown.',
            })}
          </p>
          {/* This was a <button onClick={navigate('/try')}>: it worked for humans but
              emitted no href, so the page built to convert "b1 mock exam" had no
              crawlable link to the free trial or to pricing. */}
          <Link
            to={`/${lang}/try`}
            onClick={trackStartFree}
            className="mt-7 inline-flex items-center gap-2 min-h-[48px] px-6 py-3 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-md shadow-stone-900/10 hover:bg-stone-800 active:scale-[0.97] transition-colors duration-200 cursor-pointer"
            style={DISPLAY}
          >
            {t('mockExam.cta.button', { defaultValue: 'Start a free mock exam' })}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </section>

        {/* Siblings */}
        <section className="border-t border-stone-200 bg-white" aria-labelledby="related-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2
              id="related-heading"
              className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold"
              style={DISPLAY}
            >
              {t('mockExam.related.heading', { defaultValue: 'Other exams' })}
            </h2>
            <ul className="mt-6 list-none pl-0 m-0 max-w-3xl">
              {related.map((e) => (
                <li key={e.slug} className="border-b border-stone-200 last:border-b-0">
                  <Link
                    to={`/${lang}/mock-exams/${e.slug}`}
                    className="group flex items-center justify-between gap-4 py-4 min-h-[48px] cursor-pointer"
                  >
                    <span
                      className="text-base sm:text-lg font-bold text-stone-900 group-hover:text-[#b45309] transition-colors duration-200"
                      style={DISPLAY}
                    >
                      {e.examName}
                    </span>
                    <span className="flex items-center gap-4 shrink-0">
                      <span className="hidden sm:inline text-[13px] text-stone-500 tabular-nums">
                        {t('mockExam.related.papers', {
                          count: e.variants,
                          defaultValue: `${e.variants} Modelltests`,
                        })}
                      </span>
                      <ArrowRight
                        className="w-4 h-4 text-stone-400 group-hover:text-[#b45309] transition-colors duration-200"
                        aria-hidden="true"
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </article>
    </div>
  );
};

export default MockExamLandingPage;
